import React, { useState, useEffect, useRef } from 'react';
// import { appInsights, reactPlugin } from '../../../appInsights';
// import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { jsonEditorStyles } from '../jsonEditorStyles';
import { cloneDeep } from 'lodash';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import { SelectChangeEvent } from '@mui/material';
import { Typography } from '@mui/material';
import { SelectComponent, TextComponent } from '../../uiComponents';
import { OptionProps } from '../../../view-models';
import jsonComponentsConfig from '../../../constants/jsonComponents';
import useComponentUIHook from './useComponentUIHook';
import SessionManagement, { sessionFieldsAreValid } from './sessionManagement';
import TemplatizedFieldComponent, { TemplatizedValue, getTemplatizedValue } from './templatizedFieldComponent';

interface componentProps {
  componentData: any;
  closeGritComponent: any;
  setUnsavedChanges: any;
}

const GenerateWebAuthnRegistrationUI = ({ componentData, closeGritComponent, setUnsavedChanges }: componentProps) => {
  const configData = jsonComponentsConfig.webAuthnRegistration.data;
  const {
    modifiedData,
    setModifiedData,
    checkIfSameIgnoringSubFlows: checkIfSameIgnoringSubFlows,
    allClaims,
    modifiedTemplatizations,
    setModifiedTemplatizations
  } = useComponentUIHook(componentData);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const classes = jsonEditorStyles();

  useEffect(() => {
    const _res: boolean = checkIfSameIgnoringSubFlows();
    setDisableSubmit(
      _res ||
        (!(
          modifiedData['idClaim'] &&
          modifiedData['nameClaim'] &&
          modifiedData['displayNameClaim'] &&
          modifiedData['successClaim']
        )) ||
        (!sessionFieldsAreValid(modifiedData))
    );
    setUnsavedChanges(_res);
  }, [modifiedData]);

  return (
    <div>
      <FormControl required sx={{ m: 1, minWidth: 600 }}>
        <div className={classes.flexRow}>
          <TemplatizedFieldComponent
            modifiedTemplatizations={modifiedTemplatizations}
            setModifiedTemplatizations={setModifiedTemplatizations}
            data={{
              fieldPath: 'contentDefinition',
              label: configData['contentDefinition']['displayName'],
              style: { margin: 1, width: 600 },
              value: getTemplatizedValue(modifiedData['contentDefinition']),
              variant: 'text'
            }}
            changeHandler={(e: TemplatizedValue) => {
              setModifiedData({ ...modifiedData, contentDefinition: { ...e } });
            }}
          />
        </div>
        <div className={classes.flexRow}>
          <TextComponent
            changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
              setModifiedData({
                ...modifiedData,
                ['apiUrl']: e.target.value,
              });
            }}
            data={{
              value: modifiedData.apiUrl ? modifiedData.apiUrl : '',
              label: 'Credential API Url (Premium Feature)',
              style: { margin: 1, width: 300 },
              disabled: true,
            }}
          />
        </div>
        <Typography variant="body1" sx={{ pt: 1 }}>
          Select claim containing user object ID for use in credential generation:
        </Typography>
        <div className={classes.flexRow} style={{ alignItems: 'center' }}>
          <SelectComponent
            changeHandler={(e: SelectChangeEvent<string>) => {
              setModifiedData({
                ...modifiedData,
                ['idClaim']: e.target.value,
              });
            }}
            data={{
              value: modifiedData.idClaim ? modifiedData.idClaim : '',
              label: 'Id Claim',
              required: true,
              style: { margin: 1, width: 200 },
              options: Object.keys(allClaims)
                .filter((c) => {
                  return allClaims[c]?.dataType === 'string';
                })
                .reduce((acc: OptionProps[], curVal: string) => {
                  return acc.concat({ label: curVal, value: curVal });
                }, [])
                .sort((a: OptionProps, b: OptionProps) => {
                  return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
                }),
            }}
          />
        </div>
        <Typography variant="body1" sx={{ pt: 1 }}>
          Select claim containing sign-in name to appear in authentication prompts:
        </Typography>
        <div className={classes.flexRow} style={{ alignItems: 'center' }}>
          <SelectComponent
            changeHandler={(e: SelectChangeEvent<string>) => {
              setModifiedData({
                ...modifiedData,
                ['nameClaim']: e.target.value,
              });
            }}
            data={{
              value: modifiedData.nameClaim ? modifiedData.nameClaim : '',
              label: 'Name Claim',
              required: true,
              style: { margin: 1, width: 200 },
              options: Object.keys(allClaims)
                .filter((c) => {
                  return allClaims[c]?.dataType === 'string';
                })
                .reduce((acc: OptionProps[], curVal: string) => {
                  return acc.concat({ label: curVal, value: curVal });
                }, [])
                .sort((a: OptionProps, b: OptionProps) => {
                  return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
                }),
            }}
          />
        </div>
        <Typography variant="body1" sx={{ pt: 1 }}>
          Select claim containing human-readable display name to appear in authentication prompts:
        </Typography>
        <div className={classes.flexRow} style={{ alignItems: 'center' }}>
          <SelectComponent
            changeHandler={(e: SelectChangeEvent<string>) => {
              setModifiedData({
                ...modifiedData,
                ['displayNameClaim']: e.target.value,
              });
            }}
            data={{
              value: modifiedData.displayNameClaim ? modifiedData.displayNameClaim : '',
              label: 'Display Name Claim',
              required: true,
              style: { margin: 1, width: 200 },
              options: Object.keys(allClaims)
                .filter((c) => {
                  return allClaims[c]?.dataType === 'string';
                })
                .reduce((acc: OptionProps[], curVal: string) => {
                  return acc.concat({ label: curVal, value: curVal });
                }, [])
                .sort((a: OptionProps, b: OptionProps) => {
                  return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
                }),
            }}
          />
        </div>
        <Typography variant="body1" sx={{ pt: 1 }}>
          Select claim to set with boolean success status of the WebAuthn registration ceremony:
        </Typography>
        <div className={classes.flexRow} style={{ alignItems: 'center' }}>
          <SelectComponent
            changeHandler={(e: SelectChangeEvent<string>) => {
              setModifiedData({
                ...modifiedData,
                ['successClaim']: e.target.value,
              });
            }}
            data={{
              value: modifiedData.successClaim ? modifiedData.successClaim : '',
              label: 'Success Flag Claim',
              required: true,
              style: { margin: 1, width: 200 },
              options: Object.keys(allClaims)
                .filter((c) => {
                  return allClaims[c]?.dataType === 'boolean';
                })
                .reduce((acc: OptionProps[], curVal: string) => {
                  return acc.concat({ label: curVal, value: curVal });
                }, [])
                .sort((a: OptionProps, b: OptionProps) => {
                  return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
                }),
            }}
          />
        </div>
        {configData.sessionManagement && (
          <SessionManagement
            modifiedData={modifiedData}
            changeHandler={(data: any) => setModifiedData(data)}
            sessionTypes={configData.sessionTypes}
          />
        )}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          onClick={() => {
            closeGritComponent(cloneDeep(modifiedData), modifiedTemplatizations);
          }}
          disabled={disableSubmit}
        >
          Save
        </Button>
      </FormControl>
    </div>
  );
};

// export default withAITracking(reactPlugin, GenerateWebAuthnRegistrationUI);
export default GenerateWebAuthnRegistrationUI;
